/**
 * FFmpeg WASM audio extraction strategy
 * Supports all video formats, requires SharedArrayBuffer
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

import type {
  AudioExtractionStrategy,
  ProgressCallback,
  ExtractionStrategy,
} from '../types';
import { isSharedArrayBufferSupported } from '../capabilities';

const FFMPEG_CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
const OUTPUT_MIME_TYPE = 'audio/ogg';
const OUTPUT_EXTENSION = 'ogg';

// Singleton FFmpeg instance
let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoadPromise: Promise<FFmpeg> | null = null;

/**
 * Get file extension from filename
 */
function getExtensionFromName(name: string): string {
  const ext = name.split('.').pop();
  return ext ? ext.toLowerCase() : 'media';
}

/**
 * Generate output filename
 */
function getOutputFileName(originalName: string): string {
  const baseName = originalName.replace(/\.[^/.]+$/, '') || 'audio';
  return `${baseName}.${OUTPUT_EXTENSION}`;
}

/**
 * Convert Uint8Array or string to Uint8Array
 */
function toUint8Array(data: string | Uint8Array): Uint8Array {
  if (typeof data === 'string') {
    return new TextEncoder().encode(data);
  }
  return data;
}

/**
 * Load FFmpeg WASM instance
 */
async function loadFFmpeg(
  onProgress?: (progress: number) => void
): Promise<FFmpeg> {
  if (!isSharedArrayBufferSupported()) {
    throw new Error(
      'Audio conversion requires cross-origin isolation (SharedArrayBuffer).'
    );
  }

  if (ffmpegInstance) {
    return ffmpegInstance;
  }

  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      const ffmpeg = new FFmpeg();
      const coreURL = await toBlobURL(
        `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`,
        'text/javascript'
      );
      const wasmURL = await toBlobURL(
        `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`,
        'application/wasm'
      );
      onProgress?.(0);
      await ffmpeg.load({ coreURL, wasmURL });
      onProgress?.(1);
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    })();
  }

  return ffmpegLoadPromise;
}

/**
 * Convert video to audio using FFmpeg
 */
async function convertVideoToAudio(
  ffmpeg: FFmpeg,
  file: File,
  onProgress?: (progress: number) => void
): Promise<File> {
  const inputName = `input-${Date.now()}-${Math.random().toString(36).slice(2)}.${getExtensionFromName(file.name)}`;
  const outputName = getOutputFileName(file.name);

  const progressHandler = ({ progress }: { progress: number }) => {
    onProgress?.(progress);
  };

  ffmpeg.on('progress', progressHandler);
  try {
    await ffmpeg.writeFile(inputName, await fetchFile(file));
    onProgress?.(0);
    await ffmpeg.exec([
      '-i',
      inputName,
      '-vn', // No video
      '-ac',
      '1', // Mono
      '-ar',
      '48000', // 48kHz
      '-c:a',
      'libopus', // Opus codec
      '-b:a',
      '128k', // 128kbps
      outputName,
    ]);

    const data = await ffmpeg.readFile(outputName);
    const audioBlob = new Blob([toUint8Array(data)], { type: OUTPUT_MIME_TYPE });
    return new File([audioBlob], outputName, {
      type: OUTPUT_MIME_TYPE,
      lastModified: Date.now(),
    });
  } finally {
    ffmpeg.off('progress', progressHandler);
    try {
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);
    } catch {
      // Best-effort cleanup
    }
  }
}

export class FFmpegStrategy implements AudioExtractionStrategy {
  readonly name: ExtractionStrategy = 'ffmpeg';

  async checkSupport(): Promise<boolean> {
    return isSharedArrayBufferSupported();
  }

  canHandle(file: File): boolean {
    // FFmpeg can handle any video format
    // Check by MIME type first
    if (file.type.startsWith('video/')) {
      return true;
    }

    // Also check by extension for formats that may have incorrect MIME types
    const ext = file.name.split('.').pop()?.toLowerCase();
    const videoExtensions = [
      'mp4', 'mov', 'm4v', 'mkv', 'webm', 'avi', 'wmv', 'flv',
      'ts', 'mts', 'm2ts', 'mpeg', 'mpg', 'vob', '3gp', '3g2',
    ];
    return videoExtensions.includes(ext || '');
  }

  async extract(file: File, onProgress?: ProgressCallback): Promise<File> {
    // Phase 1: Load FFmpeg (0-20%)
    const ffmpeg = await loadFFmpeg((p) => {
      onProgress?.({
        stage: 'loading',
        progress: p * 0.2,
        strategy: 'ffmpeg',
      });
    });

    // Phase 2: Convert video to audio (20-100%)
    return convertVideoToAudio(ffmpeg, file, (p) => {
      onProgress?.({
        stage: 'converting',
        progress: 0.2 + p * 0.8,
        strategy: 'ffmpeg',
      });
    });
  }
}
